<template>
  <b-modal
    v-model="data.showModal"
    size="md"
    button-size="sm"
    modal-class="template-modal"
    title-class="w-full"
    footer-class="w-full"
    no-fade
    @show="onShow"
    @hide="onHide"
    scrollable
    hide-footer
  >
    <!-- Modal header -->
    <div slot="modal-title">
      <div class="flex w-full justify-between">
        <div>Edit details</div>
        <div>
          <div v-if="savingCount > 0" class="flex gap-2 items-center">
            <b-spinner label="Spinning" small></b-spinner>
            <span>Saving..</span>
          </div>
          <div v-else-if="!canSave" class="font-bold text-red-500">
            Changes won't be saved until fixed.
          </div>
          <div v-else-if="error" class="font-bold text-red-500">
            {{ error }}
          </div>
          <div v-else>
            Changes are saved automatically.
          </div>
        </div>
      </div>
    </div>

    <!-- Modal content -->
    <div class="flex flex-col gap-2">
      <table class="table table-google table-highlight" v-if="candle">
        <tbody>
          <tr>
            <td class="row-fit">
              <span>Status</span>
            </td>
            <td>
              <div class="flex justify-end">
                  <button-select
                    v-model="candle.candle_status"
                    :options="options.candle_status"
                    size="sm"
                  />
              </div>
            </td>
          </tr>
          <tr>
            <td class="row-fit text-nowrap">
              <span :class="{ 'candle-issue': candleIssues.includes('create_date') }">
                Created
              </span>
            </td>
            <td>
              <div class="flex justify-end">
                <date-picker
                  class="date-picker"
                  v-model="candle.create_date"
                  format="DD.MM.YYYY"
                  type="date"
                  valueType="YYYY-MM-DD"
                  :clearable="false"
                  :lang="datepickerLocale"
                >
                </date-picker>
              </div>
            </td>
          </tr>

          <tr>
            <td class="row-fit">
              <span>Name</span>
            </td>
            <td>
              <b-form-input size="sm" v-model="candle.candle_name" />
            </td>
          </tr>

          <tr>
            <td class="row-fit text-nowrap">
              <span :class="{ 'candle-issue': candleIssues.includes('product_ean') }">
                EAN
              </span>
            </td>
            <td>
              <div>
                <b-form-input
                  v-model="candle.product_ean"
                  size="sm"
                  autocomplete="off"
                ></b-form-input>
              </div>
            </td>
          </tr>

          <tr>
            <td class="row-fit text-nowrap">
              <span :class="{ 'candle-issue': candleIssues.includes('product_code') }">
                NW
              </span>
            </td>
            <td>
              <div>
                <b-form-input
                  v-model="candle.product_code"
                  size="sm"
                  autocomplete="off"
                ></b-form-input>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal footer -->
    <div slot="modal-footer" class="w-full d-flex gap-4 justify-content-between items-center">
      <div class="flex w-full justify-between">
      </div>
    </div>
  </b-modal>
</template>

<script>
import _ from 'lodash';
import { datepickerLocale } from '@/helpers';
import { getCandleIssues, isCandleValid } from '@/composables/candle';

const ButtonSelect = () => import('@/components/global/ButtonSelect.vue');

export default {
  name: 'EditDetailsModal',
  components: {
    ButtonSelect,
  },
  props: {
    data: Object,
    id: {
      type: Number,
      required: true,
    },
  },
  computed: {
    isLoading() {
      return this.loadingCount > 0;
    },
    candleIssues() {
      return getCandleIssues(this.candle);
    },
    canSave() {
      return isCandleValid(this.candle);
    },
    preventMessage() {
      return 'You have invalid changes. Are you sure you want to close without saving?';
    },
    preventClose() {
      return !this.canSave || this.error !== null;
    },
    dropdowns() {
      return this.$store.state.app.dropdowns;
    },
    datepickerLocale() {
      return datepickerLocale;
    },
  },
  data() {
    return {
      savingCount: 0,
      loadingCount: 0,
      filters: {},
      options: {
        candle_status: [
          { text: 'Discarded', value: 'discarded', activeVariant: 'danger' },
          { text: 'New', value: 'new', activeVariant: 'secondary' },
          { text: 'Testing', value: 'testing', activeVariant: 'warning' },
          { text: 'Production', value: 'production', activeVariant: 'success' },
        ],
      },
      candle: null,
      error: null,
      scheduleSaving: _.debounce(() => {
        this.saveCandle();
      }, 250),
    };
  },
  methods: {
    fetchCandle() {
      this.loadingCount++;
      this.$http
        .get(`/candle/${this.id}`)
        .then((res) => {
          this.candle = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch candle: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchAll() {
      this.fetchCandle();
    },
    saveCandle() {
      if (!this.canSave || !this.candle) {
        return;
      }
      this.savingCount++;
      this.loadingCount++;
      this.$http
        .put(`/candle/${this.id}`)
        .send({ changes: this.candle })
        .then((res) => {
          this.error = null;
          this.$emit('updated', res.body);
        })
        .catch((err) => {
          this.error = err.response.text;
        })
        .finally(() => {
          this.savingCount--;
          this.loadingCount--;
        });
    },
    onShow() {
      this.fetchAll();
    },
    onClose() {
      this.data.showModal = false;
      this.$emit('close');
    },
    onHide(event) {
      if (this.preventClose && !confirm(this.preventMessage)) {
        event.preventDefault();
        return;
      }
      this.candle = null;
    },
  },
  watch: {
    candle: {
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue === null) {
          return;
        }
        this.scheduleSaving();
      },
    },
  },
};
</script>

<style lang="scss">
</style>
